import { ReactElement, useMemo } from 'react';
import { Link } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import moment from 'moment';

import { useAppSelector, useSubscription } from '~hooks/index';
import { selectAuth } from '~store/slices/auth/index';
import { isAdminUser } from '~utils/user';
import { getStringifyUrlQueryString } from '~utils/router';
import { PATHS } from '~constants/index';
import { pluralize } from '~utils/commons';
import { isPlanCorporate } from '~utils/plans';

import { SubscriptionTrialInfoProps } from './types';
import {
  SubscriptionTrialInfoContainer,
  SubscriptionTrialInfoChipContainer,
  SubscriptionTrialInfoChipProgress,
  SubscriptionTrialInfoChipLabelContainer,
  SubscriptionTrialInfoChipLabelTypography,
} from './styles';

const SubscriptionTrialInfo = ({
  size = 'small',
}: SubscriptionTrialInfoProps): ReactElement => {
  const { user } = useAppSelector(selectAuth);
  const subscription = useSubscription();

  const validFromDate = useMemo(
    () => moment(subscription?.validFromDateTime),
    [subscription?.validFromDateTime],
  );

  const validTillDate = useMemo(
    () => moment(subscription?.validTillDateTime),
    [subscription?.validTillDateTime],
  );

  const totalDaysTrial = useMemo(
    () => validTillDate.diff(validFromDate, 'days'),
    [validTillDate, validFromDate],
  );

  const restDaysTrial = useMemo(
    () => validTillDate.diff(moment().startOf('day'), 'days'),
    [validTillDate],
  );

  const isCurrentPlanCorporate = useMemo(
    () => isPlanCorporate(subscription),
    [subscription],
  );

  return (
    <SubscriptionTrialInfoContainer>
      <SubscriptionTrialInfoChipContainer size={size} restDays={restDaysTrial}>
        <SubscriptionTrialInfoChipProgress
          totalDays={totalDaysTrial}
          restDays={restDaysTrial}
        />

        <SubscriptionTrialInfoChipLabelContainer>
          <SubscriptionTrialInfoChipLabelTypography
            size={size}
            restDays={restDaysTrial}
          >
            <b>{restDaysTrial}</b>{' '}
            {pluralize({
              singular: 'día restante',
              plural: 'días restantes',
              count: restDaysTrial,
              includeCount: false,
            })}
          </SubscriptionTrialInfoChipLabelTypography>
        </SubscriptionTrialInfoChipLabelContainer>
      </SubscriptionTrialInfoChipContainer>

      {isAdminUser(user) && !isCurrentPlanCorporate && (
        <Link
          color="primary"
          underline="hover"
          fontWeight="bold"
          variant="caption"
          component={RouterLink}
          to={getStringifyUrlQueryString({
            url: PATHS.CONFIG.SUBSCRIPTIONS.BASE,
            query: {
              currentTab: 'plans-packages',
            },
          })}
        >
          ADQUIRIR UN PLAN
        </Link>
      )}
    </SubscriptionTrialInfoContainer>
  );
};

export default SubscriptionTrialInfo;
